/**
 * AdminAuthProvider - Authentication Context Provider
 * 
 * Manages authentication state, session refresh, and inactivity timeout
 * for the admin portal
 */

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentUser, signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { SESSION_TIMEOUT, TOKEN_REFRESH_INTERVAL } from '../config/constants.js';

export const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs for timers
  const inactivityTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  /**
   * Extract Cognito groups from ID token
   */
  const extractUserGroups = useCallback((idToken) => {
    try {
      if (!idToken) return [];
      
      // Decode JWT token payload
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload['cognito:groups'] || [];
    } catch (error) {
      console.error('[AdminAuth] Failed to extract groups:', error);
      return [];
    }
  }, []);

  /**
   * Check if user is admin or super admin
   */
  const checkAdminAccess = useCallback((groups) => {
    return groups.includes('Admins') || groups.includes('SuperAdmins');
  }, []);

  /**
   * Check if user is super admin
   */
  const checkSuperAdminAccess = useCallback((groups) => {
    return groups.includes('SuperAdmins');
  }, []);

  /**
   * Load current user session
   */
  const loadUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get current authenticated user
      const currentUser = await getCurrentUser();
      
      // Get auth session with tokens
      const session = await fetchAuthSession();
      
      if (!session.tokens?.idToken) {
        throw new Error('No ID token found');
      }

      // Extract groups from ID token
      const groups = extractUserGroups(session.tokens.idToken.toString());
      
      // Check admin access
      if (!checkAdminAccess(groups)) {
        throw new Error('User does not have admin access');
      }

      // Build user object
      const userData = {
        email: currentUser.signInDetails?.loginId || currentUser.username,
        userId: currentUser.userId,
        username: currentUser.username,
        groups,
        isSuperAdmin: checkSuperAdminAccess(groups),
        isAdmin: groups.includes('Admins'),
      };

      setUser(userData);
      setIsAuthenticated(true);
      
      console.log('[AdminAuth] User loaded:', userData.email, 'Groups:', groups);
      
      return userData;
    } catch (error) {
      console.error('[AdminAuth] Failed to load user:', error);
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [extractUserGroups, checkAdminAccess, checkSuperAdminAccess]);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email, password) => {
    try {
      setIsLoading(true);
      setError(null);

      // Sign in with Cognito
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
      });

      if (!isSignedIn) {
        throw new Error(`Sign in incomplete: ${nextStep.signInStep}`);
      }

      // Load user data
      await loadUser();
      
      console.log('[AdminAuth] Login successful');
    } catch (error) {
      console.error('[AdminAuth] Login failed:', error);
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [loadUser]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Clear timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }

      // Sign out from Cognito
      await signOut();
      
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      
      console.log('[AdminAuth] Logout successful');
    } catch (error) {
      console.error('[AdminAuth] Logout failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh session tokens
   */
  const refreshSession = useCallback(async () => {
    try {
      // Fetch new session (Amplify handles token refresh automatically)
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (!session.tokens?.idToken) {
        throw new Error('Failed to refresh session');
      }

      console.log('[AdminAuth] Session refreshed');
      
      return session;
    } catch (error) {
      console.error('[AdminAuth] Session refresh failed:', error);
      
      // If refresh fails, logout user
      await logout();
      throw error;
    }
  }, [logout]);

  /**
   * Reset inactivity timer
   */
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer
    inactivityTimerRef.current = setTimeout(() => {
      console.log('[AdminAuth] Session timeout due to inactivity');
      logout();
    }, SESSION_TIMEOUT);
  }, [logout]);

  /**
   * Track user activity
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initialize timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  /**
   * Setup automatic token refresh
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Set up periodic token refresh
    refreshTimerRef.current = setInterval(() => {
      refreshSession().catch(error => {
        console.error('[AdminAuth] Automatic refresh failed:', error);
      });
    }, TOKEN_REFRESH_INTERVAL);

    // Cleanup
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [isAuthenticated, refreshSession]);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    // Handle OAuth callback - check for authorization code in URL
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      
      if (code && state) {
        console.log('[AdminAuth] Processing OAuth callback...');
        // Amplify will automatically handle the code exchange
        // Just clean up the URL after
        try {
          // Wait a moment for Amplify to process the callback
          await new Promise(resolve => setTimeout(resolve, 100));
          // Remove code and state from URL without navigation
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (e) {
          console.error('[AdminAuth] Error cleaning up OAuth callback URL:', e);
        }
      }
    };
    
    handleOAuthCallback();
    
    loadUser().catch(error => {
      console.log('[AdminAuth] No active session:', error.message);
      setIsLoading(false);
    });
  }, [loadUser]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshSession,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthProvider;
